import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Plus, Search, Edit, Trash2, Upload, File, Download, ChevronDown, CheckCircle, XCircle, Loader2, AlertTriangle, Eye } from 'lucide-react';
import api, { formatErrorMessage } from '@/lib/api';
import { toast } from 'sonner';
import BulkActionsToolbar from '@/components/BulkActionsToolbar';
import CustomCheckbox from '@/components/Checkbox';
import Pagination from '@/components/Pagination';
import { Combobox } from '@/components/Combobox';
import Wizard from '@/components/Wizard';
import { useSubmitState } from '@/hooks/useSubmitState';
import { usePlateAvailability } from '@/hooks/usePlateAvailability';
import { FieldError } from '@/components/FieldError';
import { useFormPersistence, useConfirmBeforeLeave } from '@/hooks/useFormPersistence';
import { InputMask } from '@/components/InputMask';
import { validateField } from '@/utils/validation';
import { usePermissions } from '@/contexts/PermissionsContext';
import { ProtectedButton, ProtectedSection } from '@/components/rbac';

const Vehicules = () => {
  const { hasPermission } = usePermissions();
  const [vehicules, setVehicules] = useState([]);
  const [filteredVehicules, setFilteredVehicules] = useState([]);
  const [proprietaires, setProprietaires] = useState([]);
  const [typesCamion, setTypesCamion] = useState([]);
  const [chauffeurs, setChauffeurs] = useState([]);
  const [usagesPersonnalises, setUsagesPersonnalises] = useState([]);
  const [capacitesPersonnalisees, setCapacitesPersonnalisees] = useState([]);
  const [capacitesStandards, setCapacitesStandards] = useState([]);
  
  // Options prédéfinies pour équipements
  const equipementsDisponibles = [
    'Grue',
    'Sans grue',
    'Hayon',
    'Sans hayon',
    'Surbaissé',
    'Hauteur normale',
    'Ralentisseur hydraulique',
    'Porte viandes',
    'Cloison',
    'Prise de mouvement',
    'Surélevé'
  ];
  
  // Options prédéfinies pour essieux
  const essieuxDisponibles = [
    '4x2 / 2 essieux',
    '4x4 / 2 essieux',
    '6x2 / 3 essieux',
    '6x4 / 3 essieux',
    '6x6 / 3 essieux',
    '8x2 / 4 essieux',
    '8x4 / 4 essieux',
    '8x6 / 4 essieux',
    '8x8 / 4 essieux'
  ];
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [docDialogOpen, setDocDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [detailsMode, setDetailsMode] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedVehicule, setSelectedVehicule] = useState(null);
  
  // Submit state with loading management
  const { isSubmitting, withSubmit } = useSubmitState();
  
  // Field errors state (pour affichage inline)
  const [fieldErrors, setFieldErrors] = useState({});
  
  // Bulk selection state
  const [selectedIds, setSelectedIds] = useState([]);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  
  // Filtres avancés
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [filterTypeCamion, setFilterTypeCamion] = useState('all');
  const [filterProprietaire, setFilterProprietaire] = useState('all');
  const [filterPays, setFilterPays] = useState('all');
  const [filterIntraInterland, setFilterIntraInterland] = useState('all');
  const [filterCapaciteMin, setFilterCapaciteMin] = useState('');
  const [filterChauffeur, setFilterChauffeur] = useState('');
  const [filterEquipement, setFilterEquipement] = useState('all');
  const [filterEssieux, setFilterEssieux] = useState('all');
  
  // États pour usage et capacité personnalisés
  const [usagePredefini, setUsagePredefini] = useState('');
  const [usagePersonnalise, setUsagePersonnalise] = useState('');
  const [capacitePredefinie, setCapacitePredefinie] = useState('');
  const [capacitePersonnalisee, setCapacitePersonnalisee] = useState('');
  
  // Wizard state
  const [currentWizardStep, setCurrentWizardStep] = useState(0);
  
  const [formData, setFormData] = useState({
    id: '',
    type_camion_id: '',
    usage_principal: '',
    capacite_t: '',
    semi_remorque: false,
    tracteur: '',
    immatriculation: '',
    proprietaire_id: '',
    pays: 'Côte d\'Ivoire',
    intra_inter_land: 'Intraland',
    // Nouveaux champs véhicule
    equipements: [],
    nombre_essieux: '',
    // Carte grise et assurance
    marque: '',
    modele: '',
    carte_grise_ok: false,
    assurance_ok: false,
    compagnie_assurance: '',
    date_validite_assurance: '',
    date_expiration_assurance: '',
    inspection_realisee: false,
    date_inspection: '',
    remarque_inspection: '',
    date_dernier_controle_technique: '',
    date_expiration_controle_technique: '',
    photo_url: '',
  });
  
  // Initial form data for dirty check
  const initialFormData = {
    id: '',
    type_camion_id: '',
    usage_principal: '',
    capacite_t: '',
    semi_remorque: false,
    tracteur: '',
    immatriculation: '',
    proprietaire_id: '',
    pays: 'Côte d\'Ivoire',
    intra_inter_land: 'Intraland',
    equipements: [],
    nombre_essieux: '',
    marque: '',
    modele: '',
    carte_grise_ok: false,
    assurance_ok: false,
    compagnie_assurance: '',
    date_validite_assurance: '',
    date_expiration_assurance: '',
    inspection_realisee: false,
    date_inspection: '',
    remarque_inspection: '',
    date_dernier_controle_technique: '',
    date_expiration_controle_technique: '',
    photo_url: '',
  };
  
  const [docFormData, setDocFormData] = useState({
    type: 'Carte grise',
    numero: '',
    date_emission: '',
    date_expiration: '',
    file: null,
  });
  
  // Form persistence and dirty state management
  const { isDirty, clearStorage, loadFromStorage, saveToStorage } = useFormPersistence(
    'vehicule:new',
    formData,
    setFormData,
    initialFormData
  );
  
  // Confirmation before page leave if form is dirty
  useConfirmBeforeLeave(isDirty && dialogOpen, "Vous avez des modifications non enregistrées. Quitter ?");
  
  // State for close confirmation dialog
  const [closeConfirmOpen, setCloseConfirmOpen] = useState(false);
  
  // Check plate availability (only for new vehicles, not edits)
  const { isChecking: checkingPlate, isAvailable: plateAvailable, message: plateMessage } = usePlateAvailability(
    formData.immatriculation,
    !editMode && dialogOpen && formData.immatriculation.length >= 5
  );

  useEffect(() => {
    fetchData();
  }, []);
  
  // Wizard steps configuration
  const wizardSteps = [
    {
      title: "Informations de base",
      description: "Données principales du véhicule"
    },
    {
      title: "Détails transport",
      description: "Chauffeur, propriétaire et type de transport"
    },
    {
      title: "Assurance",
      description: "Informations d'assurance"
    },
    {
      title: "Inspection & Carte Grise",
      description: "Contrôle technique et documents"
    },
    {
      title: "Documents",
      description: "Upload des fichiers"
    }
  ];

  // Filtrage avec recherche basique + filtres avancés
  useEffect(() => {
    let filtered = vehicules;

    // Recherche basique améliorée
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter((v) => {
        // Recherche par immatriculation
        const matchPlate = (v.immatriculation || '').toLowerCase().includes(searchLower);
        // Recherche par pays
        const matchPays = (v.pays || '').toLowerCase().includes(searchLower);
        // Recherche par type camion
        const matchType = getTypeCamionLibelle(v.type_camion_id).toLowerCase().includes(searchLower);
        // Recherche par propriétaire
        const matchProp = getProprietaireNom(v.proprietaire_id).toLowerCase().includes(searchLower);
        // Recherche par chauffeur (nom, matricule, téléphone)
        const chauffeur = (chauffeurs || []).find(c => c.id === v.chauffeur_id);
        const matchChauffeur = chauffeur ? (
          (chauffeur.nom || '').toLowerCase().includes(searchLower) ||
          (chauffeur.matricule || '').toLowerCase().includes(searchLower) ||
          (chauffeur.telephone || '').includes(searchTerm)
        ) : false;
        
        return matchPlate || matchPays || matchType || matchProp || matchChauffeur;
      });
    }

    // Filtres avancés
    if (filterTypeCamion !== 'all') {
      filtered = filtered.filter((v) => v.type_camion_id === filterTypeCamion);
    }

    if (filterProprietaire !== 'all') {
      filtered = filtered.filter((v) => v.proprietaire_id === filterProprietaire);
    }

    if (filterPays !== 'all') {
      filtered = filtered.filter((v) => v.pays === filterPays);
    }

    if (filterIntraInterland !== 'all') {
      filtered = filtered.filter((v) => v.intra_inter_land === filterIntraInterland);
    }

    if (filterCapaciteMin !== '') {
      const minCapacite = parseFloat(filterCapaciteMin);
      filtered = filtered.filter((v) => (v.capacite_t || 0) >= minCapacite);
    }

    // Filtre par chauffeur (nom, matricule, téléphone)
    if (filterChauffeur) {
      const chauffeurLower = filterChauffeur.toLowerCase();
      filtered = filtered.filter((v) => {
        const chauffeur = (chauffeurs || []).find(c => c.id === v.chauffeur_id);
        return chauffeur && (
          (chauffeur.nom || '').toLowerCase().includes(chauffeurLower) ||
          (chauffeur.matricule || '').toLowerCase().includes(chauffeurLower) ||
          (chauffeur.telephone || '').includes(filterChauffeur)
        );
      });
    }

    // Filtre par équipement
    if (filterEquipement !== 'all') {
      filtered = filtered.filter((v) => {
        const equipements = v.equipements || [];
        return equipements.includes(filterEquipement);
      });
    }

    // Filtre par essieux
    if (filterEssieux !== 'all') {
      filtered = filtered.filter((v) => v.nombre_essieux === filterEssieux);
    }

    setFilteredVehicules(filtered);
  }, [searchTerm, vehicules, filterTypeCamion, filterProprietaire, filterPays, filterIntraInterland, filterCapaciteMin, filterChauffeur, filterEquipement, filterEssieux, chauffeurs]);

  // Pagination logic
  const totalPages = useMemo(() => Math.ceil(filteredVehicules.length / pageSize), [filteredVehicules.length, pageSize]);
  const paginatedVehicules = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredVehicules.slice(startIndex, endIndex);
  }, [filteredVehicules, currentPage, pageSize]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    setSelectedIds([]);
  };

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setCurrentPage(1);
    setSelectedIds([]);
  };

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
    setSelectedIds([]);
  }, [searchTerm, filterTypeCamion, filterProprietaire, filterPays, filterIntraInterland, filterCapaciteMin]);

  // Extraire les usages et capacités uniques des types de camions + valeurs personnalisées
  const usagesUniques = useMemo(() => {
    const usagesTypes = (typesCamion || []).map(t => t.usage_principal).filter(Boolean);
    const usagesPerso = (usagesPersonnalises || []).map(u => u.valeur);
    const allUsages = [...new Set([...usagesTypes, ...usagesPerso])];
    return allUsages.sort();
  }, [typesCamion, usagesPersonnalises]);

  const capacitesUniques = useMemo(() => {
    // Utiliser les capacités standards au lieu de celles des types de camion
    const capacitesPerso = (capacitesPersonnalisees || []).map(c => parseFloat(c.valeur)).filter(Boolean);
    const allCapacites = [...new Set([...(capacitesStandards || []), ...capacitesPerso])];
    return allCapacites.sort((a, b) => a - b);
  }, [capacitesStandards, capacitesPersonnalisees]);

  const fetchData = async () => {
    try {
      const [vehiculesRes, proprietairesRes, typesCamionRes, chauffeursRes, usagesRes, capacitesRes, capacitesStdRes] = await Promise.all([
        api.get('/vehicules'),
        api.get('/proprietaires'),
        api.get('/types-camion'),
        api.get('/chauffeurs'),
        api.get('/valeurs-personnalisees/usage'),
        api.get('/valeurs-personnalisees/capacite'),
        api.get('/capacites-standards')
      ]);
      setVehicules(vehiculesRes.data);
      setFilteredVehicules(vehiculesRes.data);
      setProprietaires(proprietairesRes.data);
      setTypesCamion(typesCamionRes.data);
      setChauffeurs(chauffeursRes.data);
      setUsagesPersonnalises(usagesRes.data);
      setCapacitesPersonnalisees(capacitesRes.data);
      setCapacitesStandards(capacitesStdRes.data || []);
      console.log('Capacités standards chargées:', capacitesStdRes.data?.length || 0);
    } catch (error) {
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Clear previous field errors
    setFieldErrors({});
    
    // Validation frontend avant soumission
    const errors = {};
    
    if (!formData.immatriculation || !formData.immatriculation.trim()) {
      errors.immatriculation = "L'immatriculation est obligatoire";
    }
    
    if (!formData.type_camion_id) {
      errors.type_camion_id = "Le type de camion est obligatoire";
    }
    
    if (!formData.capacite_t || parseFloat(formData.capacite_t) <= 0) {
      errors.capacite_t = "La capacité doit être supérieure à 0";
    }
    
    // Proprietaire_id est optionnel - supprimé la validation
    
    if (!formData.pays || !formData.pays.trim()) {
      errors.pays = "Le pays est obligatoire";
    }
    
    // Si des erreurs de validation, on arrête ici
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      toast.error("Veuillez corriger les champs en erreur");
      
      // Focus sur le premier champ en erreur
      const firstErrorField = Object.keys(errors)[0];
      const element = document.getElementById(firstErrorField);
      if (element) {
        element.focus();
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      
      return;
    }
    
    await withSubmit(
      async () => {
        // Sauvegarder les valeurs personnalisées si nécessaire
        if (usagePredefini === 'autre' && usagePersonnalise && !usagesUniques.includes(usagePersonnalise)) {
          await api.post('/valeurs-personnalisees', null, {
            params: { type: 'usage', valeur: usagePersonnalise }
          });
        }
        
        if (capacitePredefinie === 'autre' && capacitePersonnalisee && !capacitesUniques.includes(parseFloat(capacitePersonnalisee))) {
          await api.post('/valeurs-personnalisees', null, {
            params: { type: 'capacite', valeur: capacitePersonnalisee }
          });
        }

        const data = {
          ...formData,
          capacite_t: parseFloat(formData.capacite_t),
        };
        
        // Remove file fields from data (they will be uploaded separately)
        delete data.carte_grise_file;
        delete data.assurance_file;
        delete data.photo_vehicule_file;

        let vehiculeId;
        if (editMode) {
          await api.put(`/vehicules/${formData.id}`, data);
          vehiculeId = formData.id;
        } else {
          const response = await api.post('/vehicules', data);
          vehiculeId = response.data.id;
        }

        // Upload files if present
        if (formData.carte_grise_file || formData.assurance_file || formData.photo_vehicule_file) {
          toast.info('Upload des documents en cours...');
          
          // Upload carte grise
          if (formData.carte_grise_file) {
            const cgFormData = new FormData();
            cgFormData.append('file', formData.carte_grise_file);
            // Backend will use vehicle immatriculation as numero if not provided
            // and generate default dates
            
            await api.post(`/vehicules/${vehiculeId}/carte-grise`, cgFormData, {
              headers: { 'Content-Type': 'multipart/form-data' },
            });
          }
          
          // Upload assurance
          if (formData.assurance_file) {
            const assFormData = new FormData();
            assFormData.append('file', formData.assurance_file);
            // Send assurance dates if available
            if (formData.date_validite_assurance) {
              assFormData.append('date_emission', formData.date_validite_assurance);
            }
            if (formData.date_expiration_assurance) {
              assFormData.append('date_expiration', formData.date_expiration_assurance);
            }
            
            await api.post(`/vehicules/${vehiculeId}/assurance`, assFormData, {
              headers: { 'Content-Type': 'multipart/form-data' },
            });
          }
          
          // Upload photo
          if (formData.photo_vehicule_file) {
            const photoFormData = new FormData();
            photoFormData.append('file', formData.photo_vehicule_file);
            
            await api.post(`/vehicules/${vehiculeId}/photo`, photoFormData, {
              headers: { 'Content-Type': 'multipart/form-data' },
            });
          }
          
          toast.success('Documents uploadés avec succès');
        }

        return vehiculeId;
      },
      {
        successMessage: editMode ? 'Véhicule modifié avec succès' : undefined, // Pas de message par défaut en mode création
        errorMessage: 'Erreur lors de l\'opération',
        onSuccess: (result) => {
          setDialogOpen(false);
          resetForm();
          fetchData();
          
          // Message personnalisé avec le matricule pour la création
          if (!editMode && result && result.matricule) {
            toast.success(`Véhicule créé avec succès ! Matricule: ${result.matricule}`);
          } else if (editMode) {
            toast.success('Véhicule modifié avec succès');
          }
        },
        onError: (error, fieldErrs) => {
          // Appliquer les erreurs de champs si disponibles
          if (Object.keys(fieldErrs).length > 0) {
            setFieldErrors(fieldErrs);
            
            // Focus sur le premier champ en erreur
            const firstErrorField = Object.keys(fieldErrs)[0];
            const element = document.getElementById(firstErrorField);
            if (element) {
              element.focus();
              element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          }
        }
      }
    );
  };

  const handleDocSubmit = async (e) => {
    e.preventDefault();

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('vehicule_id', selectedVehicule.id);
      formDataToSend.append('type', docFormData.type);
      formDataToSend.append('numero', docFormData.numero);
      formDataToSend.append('date_emission', docFormData.date_emission);
      formDataToSend.append('date_expiration', docFormData.date_expiration);
      formDataToSend.append('file', docFormData.file);

      await api.post('/documents', formDataToSend, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      toast.success('Document ajouté avec succès');
      setDocDialogOpen(false);
      resetDocForm();
    } catch (error) {
      const errorMsg = error.response?.data?.detail 
        ? (typeof error.response.data.detail === 'string' 
          ? error.response.data.detail 
          : JSON.stringify(error.response.data.detail))
        : 'Erreur lors de l\'upload';
      toast.error(errorMsg);
    }
  };

  const handleDocUpload = async (e, docType) => {
    e.preventDefault();
    const form = e.target;
    
    try {
      const formData = new FormData();
      
      // Get form field IDs based on docType
      let numeroId, emissionId, expirationId, fileId;
      if (docType === 'carte-grise') {
        numeroId = 'cg-numero';
        emissionId = 'cg-emission';
        expirationId = 'cg-expiration';
        fileId = 'cg-file';
      } else if (docType === 'assurance') {
        numeroId = 'ass-numero';
        emissionId = 'ass-emission';
        expirationId = 'ass-expiration';
        fileId = 'ass-file';
      } else if (docType === 'permis-conduire') {
        numeroId = 'pc-numero';
        emissionId = 'pc-emission';
        expirationId = 'pc-expiration';
        fileId = 'pc-file';
      }
      
      formData.append('numero', document.getElementById(numeroId).value);
      formData.append('date_emission', document.getElementById(emissionId).value);
      formData.append('date_expiration', document.getElementById(expirationId).value);
      formData.append('file', document.getElementById(fileId).files[0]);
      
      await api.post(`/vehicules/${selectedVehicule.id}/${docType}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      
      toast.success('Document ajouté avec succès');
      form.reset();
      
      // Refresh vehicule data to show updated documents
      const response = await api.get(`/vehicules/${selectedVehicule.id}`);
      setSelectedVehicule(response.data);
      fetchData();
    } catch (error) {
      toast.error(formatErrorMessage(error, 'Erreur lors de l\'upload'));
    }
  };

  const handlePhotoUpload = async (file) => {
    if (!file) return;
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      await api.post(`/vehicules/${selectedVehicule.id}/photo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      
      toast.success('Photo ajoutée avec succès');
      
      // Refresh vehicule data to show updated photo
      const response = await api.get(`/vehicules/${selectedVehicule.id}`);
      setSelectedVehicule(response.data);
      fetchData();
    } catch (error) {
      toast.error(formatErrorMessage(error, 'Erreur lors de l\'upload de la photo'));
    }
  };

  const handleInspectionUpdate = async (e) => {
    e.preventDefault();
    
    try {
      const updateData = {
        inspection_realisee: selectedVehicule.inspection_realisee || false,
        date_inspection: document.getElementById('inspection-date')?.value || null,
        remarque_inspection: document.getElementById('inspection-remarks')?.value || null,
      };
      
      await api.put(`/vehicules/${selectedVehicule.id}`, updateData);
      toast.success('Inspection mise à jour avec succès');
      fetchData();
    } catch (error) {
      toast.error(formatErrorMessage(error, 'Erreur lors de la mise à jour'));
    }
  };


  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce véhicule ?')) return;

    try {
      await api.delete(`/vehicules/${id}`);
      toast.success('Véhicule supprimé');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // Bulk selection handlers
  const handleSelectAll = () => {
    setSelectedIds(paginatedVehicules.map(v => v.id));
  };

  const handleDeselectAll = () => {
    setSelectedIds([]);
  };

  const handleToggleSelect = (id) => {
    setSelectedIds(prev => 
      prev.includes(id) 
        ? prev.filter(selectedId => selectedId !== id)
        : [...prev, id]
    );
  };

  const handleBulkDelete = () => {
    setDeleteConfirmOpen(true);
  };

  const confirmBulkDelete = async () => {
    try {
      await api.post('/vehicules/bulk-delete', { ids: selectedIds });
      toast.success(`${selectedIds.length} véhicule(s) supprimé(s)`);
      setSelectedIds([]);
      setDeleteConfirmOpen(false);
      fetchData();
    } catch (error) {
      toast.error(formatErrorMessage(error, 'Erreur lors de la suppression'));
    }
  };


  const handleExport = async (format) => {
    try {
      const response = await api.get(`/export/vehicules?format=${format}`, {
        responseType: format === 'csv' ? 'blob' : 'json'
      });

      if (format === 'csv') {
        const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        const filename = `vehicules_export_${new Date().toISOString().split('T')[0]}.csv`;
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        
        toast.success(`✓ Export CSV réussi - ${filename}`, {
          duration: 5000,
          description: 'Le fichier a été téléchargé dans votre dossier de téléchargements'
        });
      } else {
        const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        const filename = `vehicules_export_${new Date().toISOString().split('T')[0]}.json`;
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        
        toast.success(`✓ Export JSON réussi - ${filename}`, {
          duration: 5000,
          description: 'Le fichier a été téléchargé dans votre dossier de téléchargements'
        });
      }
    } catch (error) {
      toast.error('Erreur lors de l\'export', {
        description: error.response?.data?.detail || 'Veuillez réessayer'
      });
    }
  };

  const openEditDialog = (vehicule) => {
    // Spread all vehicule fields and ensure arrays/objects are properly initialized
    setFormData({
      ...vehicule,
      equipements: vehicule.equipements || [],
      chauffeur_id: vehicule.chauffeur_id || '',
      proprietaire_id: vehicule.proprietaire_id || '',
    });
    
    // Pré-remplir les selects si valeurs existent
    if (vehicule.usage_principal && usagesUniques.includes(vehicule.usage_principal)) {
      setUsagePredefini(vehicule.usage_principal);
    } else if (vehicule.usage_principal) {
      setUsagePredefini('autre');
      setUsagePersonnalise(vehicule.usage_principal);
    }
    
    if (vehicule.capacite_t && capacitesUniques.includes(parseFloat(vehicule.capacite_t))) {
      setCapacitePredefinie(vehicule.capacite_t.toString());
    } else if (vehicule.capacite_t) {
      setCapacitePredefinie('autre');
      setCapacitePersonnalisee(vehicule.capacite_t.toString());
    }
    
    setEditMode(true);
    setDialogOpen(true);
  };

  const openDocDialog = async (vehicule) => {
    try {
      // Fetch full vehicule data including documents
      const response = await api.get(`/vehicules/${vehicule.id}`);
      setSelectedVehicule(response.data);
      setDocDialogOpen(true);
    } catch (error) {
      toast.error('Erreur lors du chargement du véhicule');
    }
  };

  const resetForm = () => {
    setFormData({
      id: '',
      type_camion_id: '',
      usage_principal: '',
      capacite_t: '',
      semi_remorque: false,
      tracteur: '',
      immatriculation: '',
      proprietaire_id: '',
      pays: 'Côte d\'Ivoire',
      intra_inter_land: 'Intraland',
      marque: '',
      modele: '',
      carte_grise_ok: false,
      assurance_ok: false,
      compagnie_assurance: '',
      date_validite_assurance: '',
      date_expiration_assurance: '',
      inspection_realisee: false,
      date_inspection: '',
      remarque_inspection: '',
      date_dernier_controle_technique: '',
      date_expiration_controle_technique: '',
      photo_url: '',
    });
    setUsagePredefini('');
    setUsagePersonnalise('');
    setCapacitePredefinie('');
    setCapacitePersonnalisee('');
    setEditMode(false);
    setCurrentWizardStep(0);
    setFieldErrors({});  // Clear field errors
  };
  
  // Wizard navigation handlers
  const handleWizardNext = () => {
    if (currentWizardStep < wizardSteps.length - 1) {
      const newStep = currentWizardStep + 1;
      setCurrentWizardStep(newStep);
      // Save current step to storage
      saveToStorage(formData, newStep);
    }
  };

  const handleWizardPrev = () => {
    if (currentWizardStep > 0) {
      const newStep = currentWizardStep - 1;
      setCurrentWizardStep(newStep);
      // Save current step to storage
      saveToStorage(formData, newStep);
    }
  };

  const canProceedToNextStep = () => {
    switch(currentWizardStep) {
      case 0: // Step 1: Basic info
        return formData.immatriculation && formData.type_camion_id && formData.capacite_t;
      case 1: // Step 2: Transport details
        return formData.pays && formData.intra_inter_land;
      case 2: // Step 3: Assurance (optional, can proceed)
        return true;
      case 3: // Step 4: Inspection (optional, can proceed)
        return true;
      case 4: // Step 5: Documents (optional, can proceed)
        return true;
      default:
        return true;
    }
  };

  const resetDocForm = () => {
    setDocFormData({
      type: 'Carte grise',
      numero: '',
      date_emission: '',
      date_expiration: '',
      file: null,
    });
  };

  const getProprietaireNom = (id) => {
    const prop = (proprietaires || []).find((p) => p.id === id);
    return prop ? prop.nom : 'N/A';
  };

  const getChauffeurNom = (id) => {
    if (!id) return '-';
    const chauffeur = (chauffeurs || []).find((c) => c.id === id);
    return chauffeur ? chauffeur.nom : '-';
  };


  const getTypeCamionLibelle = (id) => {
    const type = (typesCamion || []).find((t) => t.id === id);
    return type ? type.libelle : 'N/A';
  };
  
  // Render wizard step content
  const renderWizardStepContent = () => {
    switch(currentWizardStep) {
      case 0: // Step 1: Informations de base
        return (
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="immatriculation">Immatriculation *</Label>
              <div className="relative">
                <InputMask
                  mask="immatriculation"
                  id="immatriculation"
                  value={formData.immatriculation}
                  onChange={(e) => {
                    setFormData({ ...formData, immatriculation: e.target.value });
                    // Clear error when user types
                    if (fieldErrors.immatriculation) {
                      setFieldErrors({...fieldErrors, immatriculation: undefined});
                    }
                  }}
                  required
                  data-testid="vehicule-immat-input"
                  className={fieldErrors.immatriculation ? 'border-red-500' : ''}
                />
                {/* Availability badge */}
                {!editMode && formData.immatriculation.length >= 5 && (
                  <div className="absolute right-2 top-1/2 -translate-y-1/2">
                    {checkingPlate ? (
                      <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
                    ) : plateAvailable === true ? (
                      <div className="flex items-center gap-1 text-xs bg-green-100 text-green-700 px-2 py-1 rounded">
                        <CheckCircle className="h-3 w-3" />
                        <span>Disponible</span>
                      </div>
                    ) : plateAvailable === false ? (
                      <div className="flex items-center gap-1 text-xs bg-red-100 text-red-700 px-2 py-1 rounded">
                        <XCircle className="h-3 w-3" />
                        <span>Déjà utilisée</span>
                      </div>
                    ) : null}
                  </div>
                )}
              </div>
              <p className="text-xs text-gray-500">
                Formats acceptés: CI-ABJ-1234-AA (Côte d'Ivoire), BF-OUA-5678-BC (Burkina Faso), ou format standard du pays d'origine
              </p>
              <FieldError error={fieldErrors.immatriculation} />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="type_camion_id">Type de camion *</Label>
              <Combobox
                options={(typesCamion || []).map(type => ({
                  value: type.id,
                  label: type.type_camion || type.libelle
                }))}
                value={formData.type_camion_id}
                onValueChange={(value) => {
                  setFormData({ ...formData, type_camion_id: value });
                  if (fieldErrors.type_camion_id) {
                    setFieldErrors({...fieldErrors, type_camion_id: undefined});
                  }
                }}
                placeholder="Sélectionnez un type"
                searchPlaceholder="Rechercher..."
                emptyText="Aucun type trouvé"
              />
              <FieldError error={fieldErrors.type_camion_id} />
            </div>

            <div className="space-y-2">
              <Label htmlFor="capacite_select">Capacité (tonnes) *</Label>
              <Select
                value={capacitePredefinie}
                onValueChange={(value) => {
                  setCapacitePredefinie(value);
                  if (value === 'autre') {
                    setFormData({ ...formData, capacite_t: '' });
                  } else if (value) {
                    setFormData({ ...formData, capacite_t: value });
                    setCapacitePersonnalisee('');
                  }
                  if (fieldErrors.capacite_t) {
                    setFieldErrors({...fieldErrors, capacite_t: undefined});
                  }
                }}
              >
                <SelectTrigger className={fieldErrors.capacite_t ? 'border-red-500' : ''}>
                  <SelectValue placeholder="Choisir une capacité" />
                </SelectTrigger>
                <SelectContent>
                  {capacitesUniques.map((cap) => (
                    <SelectItem key={cap} value={cap.toString()}>
                      {cap} T
                    </SelectItem>
                  ))}
                  <SelectItem value="autre">✏️ Autre</SelectItem>
                </SelectContent>
              </Select>
              <FieldError error={fieldErrors.capacite_t} />
            </div>

            {capacitePredefinie === 'autre' && (
              <div className="space-y-2">
                <Label htmlFor="capacite_personnalisee">Capacité personnalisée (T) *</Label>
                <Input
                  id="capacite_personnalisee"
                  type="number"
                  step="0.1"
                  value={capacitePersonnalisee}
                  onChange={(e) => {
                    setCapacitePersonnalisee(e.target.value);
                    setFormData({ ...formData, capacite_t: e.target.value });
                    if (fieldErrors.capacite_t) {
                      setFieldErrors({...fieldErrors, capacite_t: undefined});
                    }
                  }}
                  placeholder="Ex: 22.5"
                  required
                  className={fieldErrors.capacite_t ? 'border-red-500' : ''}
                />
                <FieldError error={fieldErrors.capacite_t} />
              </div>
            )}
            
            <div className="space-y-2">
              <Label htmlFor="usage_select">Usage principal</Label>
              <Select
                value={usagePredefini}
                onValueChange={(value) => {
                  setUsagePredefini(value);
                  if (value === 'autre') {
                    setFormData({ ...formData, usage_principal: '' });
                  } else if (value) {
                    setFormData({ ...formData, usage_principal: value });
                    setUsagePersonnalise('');
                  }
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Choisir un usage" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">-- Aucun --</SelectItem>
                  {usagesUniques.map((usage) => (
                    <SelectItem key={usage} value={usage}>
                      {usage}
                    </SelectItem>
                  ))}
                  <SelectItem value="autre">✏️ Autre</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {usagePredefini === 'autre' && (
              <div className="space-y-2">
                <Label htmlFor="usage_personnalise">Usage personnalisé</Label>
                <Input
                  id="usage_personnalise"
                  value={usagePersonnalise}
                  onChange={(e) => {
                    setUsagePersonnalise(e.target.value);
                    setFormData({ ...formData, usage_principal: e.target.value });
                  }}
                  placeholder="Ex: Transport spécialisé..."
                />
              </div>
            )}
          </div>
        );
        
      case 1: // Step 2: Détails transport
        return (
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="chauffeur_id">Chauffeur associé</Label>
              <Combobox
                options={[
                  { value: "none", label: "Aucun" },
                  ...(chauffeurs || []).map(ch => ({ 
                    value: ch.id, 
                    label: `${ch.matricule || 'N/A'} - ${ch.nom} - ${ch.telephone || 'N/A'} - ${ch.pays}`,
                    searchText: `${ch.matricule || ''} ${ch.nom || ''} ${ch.telephone || ''} ${ch.pays || ''}`.toLowerCase()
                  }))
                ]}
                value={formData.chauffeur_id || "none"}
                onValueChange={(value) => setFormData({ ...formData, chauffeur_id: value === "none" ? '' : value })}
                placeholder="Rechercher un chauffeur"
                searchPlaceholder="Nom, matricule ou téléphone..."
                emptyText="Aucun chauffeur trouvé"
              />
              <p className="text-xs text-gray-500">Recherchez par nom, matricule (BAO-CH-XXXX) ou téléphone</p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="proprietaire_id">Partenaire / Société (Optionnel)</Label>
              <Combobox
                options={[
                  { value: "none", label: "Aucun" },
                  ...(proprietaires || []).map(prop => ({ 
                    value: prop.id, 
                    label: `${prop.nom} - ${prop.pays}` 
                  }))
                ]}
                value={formData.proprietaire_id || "none"}
                onValueChange={(value) => {
                  setFormData({ ...formData, proprietaire_id: value === "none" ? '' : value });
                  if (fieldErrors.proprietaire_id) {
                    setFieldErrors({...fieldErrors, proprietaire_id: undefined});
                  }
                }}
                placeholder="Aucun (véhicule sans partenaire)"
                searchPlaceholder="Taper le nom..."
                emptyText="Aucun partenaire trouvé"
              />
              <FieldError error={fieldErrors.proprietaire_id} />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="pays">Pays d'origine du véhicule *</Label>
              <Select
                value={formData.pays}
                onValueChange={(value) => {
                  setFormData({ ...formData, pays: value });
                  if (fieldErrors.pays) {
                    setFieldErrors({...fieldErrors, pays: undefined});
                  }
                }}
              >
                <SelectTrigger id="pays" className={fieldErrors.pays ? 'border-red-500' : ''} data-testid="vehicule-pays-select">
                  <SelectValue placeholder="Sélectionner un pays" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Bénin">Bénin</SelectItem>
                  <SelectItem value="Burkina Faso">Burkina Faso</SelectItem>
                  <SelectItem value="Cap-Vert">Cap-Vert</SelectItem>
                  <SelectItem value="Côte d'Ivoire">Côte d'Ivoire</SelectItem>
                  <SelectItem value="Gambie">Gambie</SelectItem>
                  <SelectItem value="Ghana">Ghana</SelectItem>
                  <SelectItem value="Guinée">Guinée</SelectItem>
                  <SelectItem value="Guinée-Bissau">Guinée-Bissau</SelectItem>
                  <SelectItem value="Liberia">Liberia</SelectItem>
                  <SelectItem value="Mali">Mali</SelectItem>
                  <SelectItem value="Niger">Niger</SelectItem>
                  <SelectItem value="Nigeria">Nigeria</SelectItem>
                  <SelectItem value="Sénégal">Sénégal</SelectItem>
                  <SelectItem value="Sierra Leone">Sierra Leone</SelectItem>
                  <SelectItem value="Togo">Togo</SelectItem>
                  <SelectItem value="Autre">Autre pays</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500">
                Sélectionnez le pays d'origine du véhicule (immatriculation)
              </p>
              <FieldError error={fieldErrors.pays} />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="intra_inter_land">Type transport *</Label>
              <Select
                value={formData.intra_inter_land}
                onValueChange={(value) => setFormData({ ...formData, intra_inter_land: value })}
              >
                <SelectTrigger data-testid="vehicule-transport-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Intraland">Intraland</SelectItem>
                  <SelectItem value="Interland">Interland</SelectItem>
                  <SelectItem value="Les deux (Intra-Inter)">Les deux (Intra-Inter)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="tracteur">Tracteur</Label>
              <Input
                id="tracteur"
                value={formData.tracteur}
                onChange={(e) => setFormData({ ...formData, tracteur: e.target.value })}
                placeholder="Immatriculation du tracteur"
                data-testid="vehicule-tracteur-input"
              />
            </div>
            
            <div className="flex items-center space-x-2 pt-6">
              <Checkbox
                id="semi_remorque"
                checked={formData.semi_remorque}
                onCheckedChange={(checked) => setFormData({ ...formData, semi_remorque: checked })}
                data-testid="vehicule-semi-checkbox"
              />
              <Label htmlFor="semi_remorque" className="cursor-pointer">Semi-remorque</Label>
            </div>
          </div>
        );
        
      case 2: // Step 3: Assurance
        return (
          <div className="grid grid-cols-2 gap-4">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="assurance_ok"
                checked={formData.assurance_ok}
                onCheckedChange={(checked) => setFormData({ ...formData, assurance_ok: checked })}
              />
              <Label htmlFor="assurance_ok" className="cursor-pointer">Assurance OK</Label>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="compagnie_assurance">Compagnie d'assurance</Label>
              <Input
                id="compagnie_assurance"
                value={formData.compagnie_assurance}
                onChange={(e) => setFormData({ ...formData, compagnie_assurance: e.target.value })}
                placeholder="Ex: NSIA, SUNU..."
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="date_validite_assurance">Date validité</Label>
              <Input
                id="date_validite_assurance"
                type="date"
                value={formData.date_validite_assurance}
                onChange={(e) => setFormData({ ...formData, date_validite_assurance: e.target.value })}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="date_expiration_assurance">Date expiration</Label>
              <Input
                id="date_expiration_assurance"
                type="date"
                value={formData.date_expiration_assurance}
                onChange={(e) => setFormData({ ...formData, date_expiration_assurance: e.target.value })}
              />
            </div>
          </div>
        );
        
      case 3: // Step 4: Inspection & Carte Grise
        return (
          <div className="space-y-6">
            <div>
              <h3 className="font-semibold text-md mb-3">🚗 Caractéristiques du véhicule</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="marque">Marque</Label>
                  <Input
                    id="marque"
                    value={formData.marque}
                    onChange={(e) => setFormData({ ...formData, marque: e.target.value })}
                    placeholder="Ex: Mercedes, Volvo..."
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="modele">Modèle</Label>
                  <Input
                    id="modele"
                    value={formData.modele}
                    onChange={(e) => setFormData({ ...formData, modele: e.target.value })}
                    placeholder="Ex: Actros, FH..."
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="nombre_essieux">Configuration essieux</Label>
                  <Select
                    value={formData.nombre_essieux}
                    onValueChange={(value) => setFormData({ ...formData, nombre_essieux: value })}
                  >
                    <SelectTrigger id="nombre_essieux">
                      <SelectValue placeholder="Sélectionner" />
                    </SelectTrigger>
                    <SelectContent>
                      {essieuxDisponibles.map((essieu) => (
                        <SelectItem key={essieu} value={essieu}>{essieu}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="carte_grise_ok"
                    checked={formData.carte_grise_ok}
                    onCheckedChange={(checked) => setFormData({ ...formData, carte_grise_ok: checked })}
                  />
                  <Label htmlFor="carte_grise_ok" className="cursor-pointer">Carte grise OK</Label>
                </div>
                
                <div className="col-span-2 space-y-2">
                  <Label>Équipements (sélection multiple)</Label>
                  <div className="grid grid-cols-2 gap-2 p-3 border rounded-md max-h-48 overflow-y-auto">
                    {equipementsDisponibles.map((equipement) => (
                      <div key={equipement} className="flex items-center space-x-2">
                        <Checkbox
                          id={`equip-${equipement}`}
                          checked={(formData.equipements || []).includes(equipement)}
                          onCheckedChange={(checked) => {
                            const currentEquipements = formData.equipements || [];
                            const newEquipements = checked
                              ? [...currentEquipements, equipement]
                              : currentEquipements.filter(e => e !== equipement);
                            setFormData({ ...formData, equipements: newEquipements });
                          }}
                        />
                        <Label htmlFor={`equip-${equipement}`} className="cursor-pointer text-sm">
                          {equipement}
                        </Label>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            
            <div>
              <h3 className="font-semibold text-md mb-3">🔧 Inspection & Contrôle Technique</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="inspection_realisee"
                    checked={formData.inspection_realisee}
                    onCheckedChange={(checked) => setFormData({ ...formData, inspection_realisee: checked })}
                  />
                  <Label htmlFor="inspection_realisee" className="cursor-pointer">Inspection réalisée</Label>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="date_inspection">Date inspection</Label>
                  <Input
                    id="date_inspection"
                    type="date"
                    value={formData.date_inspection}
                    onChange={(e) => setFormData({ ...formData, date_inspection: e.target.value })}
                  />
                </div>
                
                <div className="col-span-2 space-y-2">
                  <Label htmlFor="remarque_inspection">Remarque inspection</Label>
                  <Input
                    id="remarque_inspection"
                    value={formData.remarque_inspection}
                    onChange={(e) => setFormData({ ...formData, remarque_inspection: e.target.value })}
                    placeholder="Notes sur l'inspection..."
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="date_dernier_controle_technique">Date dernier contrôle</Label>
                  <Input
                    id="date_dernier_controle_technique"
                    type="date"
                    value={formData.date_dernier_controle_technique}
                    onChange={(e) => setFormData({ ...formData, date_dernier_controle_technique: e.target.value })}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="date_expiration_controle_technique">Date expiration contrôle</Label>
                  <Input
                    id="date_expiration_controle_technique"
                    type="date"
                    value={formData.date_expiration_controle_technique}
                    onChange={(e) => setFormData({ ...formData, date_expiration_controle_technique: e.target.value })}
                  />
                </div>
              </div>
            </div>
          </div>
        );
        
      case 4: // Step 5: Documents
        return (
          <div className="space-y-4 bg-blue-50 p-4 rounded-lg">
            <p className="text-sm text-gray-600 mb-4">
              📁 Uploadez les documents du véhicule (optionnel)
            </p>
            
            <div className="space-y-2">
              <Label htmlFor="carte_grise_file">📄 Carte grise (PDF/PNG/JPEG)</Label>
              <Input
                id="carte_grise_file"
                type="file"
                accept=".pdf,.png,.jpg,.jpeg"
                onChange={(e) => setFormData({ ...formData, carte_grise_file: e.target.files[0] })}
                className="cursor-pointer"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="assurance_file">📄 Attestation assurance (PDF/PNG/JPEG)</Label>
              <Input
                id="assurance_file"
                type="file"
                accept=".pdf,.png,.jpg,.jpeg"
                onChange={(e) => setFormData({ ...formData, assurance_file: e.target.files[0] })}
                className="cursor-pointer"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="photo_vehicule">📸 Photo du véhicule</Label>
              <Input
                id="photo_vehicule"
                type="file"
                accept="image/*"
                onChange={(e) => setFormData({ ...formData, photo_vehicule_file: e.target.files[0] })}
                className="cursor-pointer"
              />
              <p className="text-xs text-gray-500">Formats: JPG, PNG, WEBP</p>
            </div>
          </div>
        );
        
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  return (
    <div className="p-8 space-y-6" data-testid="vehicules-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-gray-900">Véhicules</h1>
          <p className="text-gray-600 mt-2">Gestion des véhicules et documents</p>
        </div>
        <div className="flex items-center gap-3">
          <ProtectedButton
            resource="vehicules"
            action="export"
            onClick={() => handleExport('csv')}
            variant="outline"
            className="border-emerald-600 text-emerald-600 hover:bg-emerald-50"
            data-testid="export-vehicules-csv-btn"
          >
            <Download className="h-4 w-4 mr-2" />
            Exporter CSV
          </ProtectedButton>
          
          <ProtectedButton
            resource="vehicules"
            action="export"
            onClick={() => handleExport('json')}
            variant="outline"
            className="border-blue-600 text-blue-600 hover:bg-blue-50"
            data-testid="export-vehicules-json-btn"
          >
            <Download className="h-4 w-4 mr-2" />
            Exporter JSON
          </ProtectedButton>
          
          <ProtectedButton
            resource="vehicules"
            action="create"
            onClick={() => {
              // Check if there's a saved draft
              const savedDraft = loadFromStorage();
              
              if (savedDraft && savedDraft.formData) {
                // Ask if user wants to restore draft
                if (window.confirm("Un brouillon a été trouvé. Voulez-vous le charger ?")) {
                  setFormData(savedDraft.formData);
                  setCurrentWizardStep(savedDraft.currentStep || 0);
                  toast.success('Brouillon chargé');
                } else {
                  resetForm();
                  clearStorage();
                }
              } else {
                resetForm();
              }
              
              setDialogOpen(true);
            }}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="add-vehicule-btn"
          >
            <Plus className="h-5 w-5 mr-2" />
            Ajouter un véhicule
          </ProtectedButton>
        </div>
      </div>

      {/* Search */}
      <Card className="border-0 shadow-md">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <Input
              placeholder="Rechercher par plaque, chauffeur (nom/matricule/tél), type..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 h-11"
              data-testid="search-vehicule-input"
            />
          </div>
        </CardContent>
      </Card>

      {/* Advanced Filters */}
      <Card className="border-0 shadow-md">
        <CardHeader>
          <div className="flex items-center justify-between cursor-pointer" onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}>
            <h3 className="text-lg font-semibold">Filtres avancés</h3>
            <ChevronDown className={`h-5 w-5 transition-transform ${showAdvancedFilters ? 'rotate-180' : ''}`} />
          </div>
        </CardHeader>
        {showAdvancedFilters && (
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label>Type de camion</Label>
                <Select value={filterTypeCamion} onValueChange={setFilterTypeCamion}>
                  <SelectTrigger>
                    <SelectValue placeholder="Tous les types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous les types</SelectItem>
                    {(typesCamion || []).map((type) => (
                      <SelectItem key={type.id} value={type.id}>
                        {type.type_camion || type.libelle}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Partenaire / Société</Label>
                <Combobox
                  options={[
                    { value: "all", label: "Tous les partenaires" },
                    ...(proprietaires || []).map(p => ({ 
                      value: p.id, 
                      label: p.nom 
                    }))
                  ]}
                  value={filterProprietaire}
                  onValueChange={setFilterProprietaire}
                  placeholder="Tous les partenaires"
                  searchPlaceholder="Rechercher..."
                  emptyText="Aucun partenaire trouvé"
                />
              </div>

              <div>
                <Label>Pays</Label>
                <Combobox
                  options={[
                    { value: "all", label: "Tous les pays" },
                    { value: "Bénin", label: "Bénin" },
                    { value: "Burkina Faso", label: "Burkina Faso" },
                    { value: "Cap-Vert", label: "Cap-Vert" },
                    { value: "Côte d'Ivoire", label: "Côte d'Ivoire" },
                    { value: "Gambie", label: "Gambie" },
                    { value: "Ghana", label: "Ghana" },
                    { value: "Guinée", label: "Guinée" },
                    { value: "Guinée-Bissau", label: "Guinée-Bissau" },
                    { value: "Liberia", label: "Liberia" },
                    { value: "Mali", label: "Mali" },
                    { value: "Mauritanie", label: "Mauritanie" },
                    { value: "Niger", label: "Niger" },
                    { value: "Nigeria", label: "Nigeria" },
                    { value: "Sénégal", label: "Sénégal" },
                    { value: "Sierra Leone", label: "Sierra Leone" },
                    { value: "Togo", label: "Togo" }
                  ]}
                  value={filterPays}
                  onValueChange={setFilterPays}
                  placeholder="Tous les pays"
                  searchPlaceholder="Rechercher un pays..."
                  emptyText="Aucun pays trouvé"
                />
              </div>

              <div>
                <Label>Type de transport</Label>
                <Select value={filterIntraInterland} onValueChange={setFilterIntraInterland}>
                  <SelectTrigger>
                    <SelectValue placeholder="Tous les types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous les types</SelectItem>
                    <SelectItem value="Intraland">Intraland</SelectItem>
                    <SelectItem value="Interland">Interland</SelectItem>
                    <SelectItem value="Les deux (Intra-Inter)">Les deux (Intra-Inter)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Capacité minimale (T)</Label>
                <Input
                  type="number"
                  placeholder="Ex: 20"
                  value={filterCapaciteMin}
                  onChange={(e) => setFilterCapaciteMin(e.target.value)}
                />
              </div>

              <div>
                <Label>Chauffeur (Nom, Matricule, Téléphone)</Label>
                <Input
                  placeholder="Rechercher un chauffeur..."
                  value={filterChauffeur}
                  onChange={(e) => setFilterChauffeur(e.target.value)}
                />
              </div>

              <div>
                <Label>Équipement</Label>
                <Select value={filterEquipement} onValueChange={setFilterEquipement}>
                  <SelectTrigger>
                    <SelectValue placeholder="Tous les équipements" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous les équipements</SelectItem>
                    {equipementsDisponibles.map((eq) => (
                      <SelectItem key={eq} value={eq}>{eq}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Essieux</Label>
                <Select value={filterEssieux} onValueChange={setFilterEssieux}>
                  <SelectTrigger>
                    <SelectValue placeholder="Tous les essieux" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous les essieux</SelectItem>
                    {essieuxDisponibles.map((essieu) => (
                      <SelectItem key={essieu} value={essieu}>{essieu}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-end">
                <Button
                  variant="outline"
                  onClick={() => {
                    setFilterTypeCamion('all');
                    setFilterProprietaire('all');
                    setFilterPays('all');
                    setFilterIntraInterland('all');
                    setFilterCapaciteMin('');
                    setFilterChauffeur('');
                    setFilterEquipement('all');
                    setFilterEssieux('all');
                  }}
                  className="w-full"
                >
                  Réinitialiser les filtres
                </Button>
              </div>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Table */}
      <Card className="border-0 shadow-md">
        <CardHeader>
          <CardTitle>Liste des véhicules ({filteredVehicules.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {/* Bulk Actions Toolbar */}
          <BulkActionsToolbar
            selectedCount={selectedIds.length}
            totalCount={paginatedVehicules.length}
            onSelectAll={handleSelectAll}
            onDeselectAll={handleDeselectAll}
            onBulkDelete={handleBulkDelete}
            entityName="véhicule"
          />

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="border-b border-gray-200">
                <tr>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700 w-12">
                    <CustomCheckbox
                      checked={selectedIds.length === paginatedVehicules.length && paginatedVehicules.length > 0}
                      onChange={(checked) => checked ? handleSelectAll() : handleDeselectAll()}
                    />
                  </th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Matricule</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Immatriculation</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Type</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Capacité (T)</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Chauffeur associé</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Propriétaire</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Pays</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Type transport</th>
                  <th className="text-right py-3 px-4 font-semibold text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedVehicules.map((vehicule) => (
                  <tr key={vehicule.id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                    <td className="py-3 px-4">
                      <CustomCheckbox
                        checked={selectedIds.includes(vehicule.id)}
                        onChange={() => handleToggleSelect(vehicule.id)}
                      />
                    </td>
                    <td className="py-3 px-4">
                      <span className="font-mono text-xs text-gray-600">{vehicule.matricule || '-'}</span>
                    </td>
                    <td className="py-3 px-4 font-medium text-gray-900">{vehicule.immatriculation}</td>
                    <td className="py-3 px-4 text-gray-700">{getTypeCamionLibelle(vehicule.type_camion_id)}</td>
                    <td className="py-3 px-4 text-gray-700">{vehicule.capacite_t}</td>
                    <td className="py-3 px-4 text-gray-700">{getChauffeurNom(vehicule.chauffeur_id)}</td>
                    <td className="py-3 px-4 text-gray-700">{getProprietaireNom(vehicule.proprietaire_id)}</td>
                    <td className="py-3 px-4 text-gray-700">{vehicule.pays}</td>
                    <td className="py-3 px-4 text-gray-700">{vehicule.intra_inter_land}</td>
                    <td className="py-3 px-4">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            setSelectedVehicule(vehicule);
                            setDetailsMode(true);
                            setEditMode(false);
                            setDialogOpen(true);
                          }}
                          title="Voir les détails"
                          className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openDocDialog(vehicule)}
                          title="Ajouter un document"
                          data-testid={`upload-doc-${vehicule.id}`}
                        >
                          <Upload className="h-4 w-4 text-emerald-600" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openEditDialog(vehicule)}
                          data-testid={`edit-vehicule-${vehicule.id}`}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(vehicule.id)}
                          data-testid={`delete-vehicule-${vehicule.id}`}
                        >
                          <Trash2 className="h-4 w-4 text-red-600" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filteredVehicules.length === 0 && (
              <div className="text-center py-12 text-gray-500">
                Aucun véhicule trouvé
              </div>
            )}
          </div>

          {/* Pagination */}
          {filteredVehicules.length > 0 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              pageSize={pageSize}
              totalItems={filteredVehicules.length}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
            />
          )}
        </CardContent>
      </Card>

      {/* Vehicule Dialog with Wizard */}
      <Dialog open={dialogOpen} onOpenChange={(open) => {
        // If closing and form is dirty, show confirmation
        if (!open && isDirty && !isSubmitting) {
          setCloseConfirmOpen(true);
        } else {
          setDialogOpen(open);
          if (!open) {
            // Don't reset step immediately - allow reload
            clearStorage();
          }
        }
      }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="vehicule-dialog">
          {detailsMode && selectedVehicule ? (
            // Mode détails - Affichage structuré
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  🚚 Détails du véhicule - {selectedVehicule.immatriculation}
                </DialogTitle>
                <DialogDescription>
                  Matricule: {selectedVehicule.matricule || 'N/A'}
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4 py-4">
                {/* Informations de base */}
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    📋 Informations générales
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <span className="text-sm text-gray-600">Immatriculation:</span>
                      <p className="font-medium">{selectedVehicule.immatriculation}</p>
                    </div>
                    <div>
                      <span className="text-sm text-gray-600">Matricule:</span>
                      <p className="font-medium">{selectedVehicule.matricule || '-'}</p>
                    </div>
                    <div>
                      <span className="text-sm text-gray-600">Type de camion:</span>
                      <p className="font-medium">{getTypeCamionLibelle(selectedVehicule.type_camion_id)}</p>
                    </div>
                    <div>
                      <span className="text-sm text-gray-600">Capacité:</span>
                      <p className="font-medium">{selectedVehicule.capacite_t} tonnes</p>
                    </div>
                    <div>
                      <span className="text-sm text-gray-600">Pays:</span>
                      <p className="font-medium">{selectedVehicule.pays || '-'}</p>
                    </div>
                    <div>
                      <span className="text-sm text-gray-600">Type transport:</span>
                      <p className="font-medium">{selectedVehicule.intra_inter_land || '-'}</p>
                    </div>
                  </div>
                </div>
                
                {/* Chauffeur et Propriétaire */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h3 className="font-semibold text-blue-900 mb-3 flex items-center gap-2">
                    👥 Chauffeur & Propriétaire
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <span className="text-sm text-gray-600">Chauffeur associé:</span>
                      <p className="font-medium">{getChauffeurNom(selectedVehicule.chauffeur_id)}</p>
                    </div>
                    <div>
                      <span className="text-sm text-gray-600">Propriétaire:</span>
                      <p className="font-medium">{getProprietaireNom(selectedVehicule.proprietaire_id)}</p>
                    </div>
                  </div>
                </div>
                
                {/* Caractéristiques techniques */}
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <h3 className="font-semibold text-amber-900 mb-3 flex items-center gap-2">
                    🔧 Caractéristiques techniques
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <span className="text-sm text-gray-600">Marque:</span>
                      <p className="font-medium">{selectedVehicule.marque || '-'}</p>
                    </div>
                    <div>
                      <span className="text-sm text-gray-600">Modèle:</span>
                      <p className="font-medium">{selectedVehicule.modele || '-'}</p>
                    </div>
                    <div>
                      <span className="text-sm text-gray-600">Configuration essieux:</span>
                      <p className="font-medium">{selectedVehicule.nombre_essieux || '-'}</p>
                    </div>
                    <div>
                      <span className="text-sm text-gray-600">Équipements:</span>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {(selectedVehicule.equipements || []).length > 0 ? (
                          selectedVehicule.equipements.map((eq, i) => (
                            <span key={i} className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                              {eq}
                            </span>
                          ))
                        ) : (
                          <span className="text-sm">-</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Documents */}
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <h3 className="font-semibold text-green-900 mb-3 flex items-center gap-2">
                    📄 Documents & Conformité
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="flex items-center gap-2">
                      {selectedVehicule.carte_grise_ok ? (
                        <><CheckCircle className="h-4 w-4 text-green-600" /><span className="text-sm">Carte grise OK</span></>
                      ) : (
                        <><XCircle className="h-4 w-4 text-red-600" /><span className="text-sm">Carte grise manquante</span></>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {selectedVehicule.inspection_realisee ? (
                        <><CheckCircle className="h-4 w-4 text-green-600" /><span className="text-sm">Inspection réalisée</span></>
                      ) : (
                        <><XCircle className="h-4 w-4 text-red-600" /><span className="text-sm">Inspection non faite</span></>
                      )}
                    </div>
                    <div>
                      <span className="text-sm text-gray-600">Assurance:</span>
                      <p className="font-medium">{selectedVehicule.assurance || '-'}</p>
                      {selectedVehicule.date_expiration_assurance && (
                        <p className="text-xs text-gray-500">Expire le: {new Date(selectedVehicule.date_expiration_assurance).toLocaleDateString('fr-FR')}</p>
                      )}
                    </div>
                    <div>
                      <span className="text-sm text-gray-600">Contrôle technique:</span>
                      {selectedVehicule.date_expiration_controle_technique ? (
                        <p className="font-medium">Expire le: {new Date(selectedVehicule.date_expiration_controle_technique).toLocaleDateString('fr-FR')}</p>
                      ) : (
                        <p className="font-medium">-</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              
              <DialogFooter>
                <Button 
                  variant="outline"
                  onClick={() => {
                    setDetailsMode(false);
                    setDialogOpen(false);
                    setSelectedVehicule(null);
                  }}
                >
                  Fermer
                </Button>
                <Button
                  onClick={() => {
                    setDetailsMode(false);
                    setEditMode(true);
                    // Populate form with selected vehicle data
                    setFormData({
                      ...selectedVehicule,
                      chauffeur_id: selectedVehicule.chauffeur_id || '',
                      proprietaire_id: selectedVehicule.proprietaire_id || '',
                      equipements: selectedVehicule.equipements || [],
                    });
                  }}
                  className="bg-emerald-600 hover:bg-emerald-700"
                >
                  <Edit className="h-4 w-4 mr-2" />
                  Modifier
                </Button>
              </DialogFooter>
            </>
          ) : (
            // Mode création/édition - Wizard
            <form onSubmit={handleSubmit}>
              <DialogHeader>
                <DialogTitle>{editMode ? 'Modifier le véhicule' : 'Ajouter un véhicule'}</DialogTitle>
                <DialogDescription>
                  Remplissez les informations du véhicule en suivant les étapes
                </DialogDescription>
              </DialogHeader>
              
              <Wizard
                steps={wizardSteps}
                currentStep={currentWizardStep}
                onNext={handleWizardNext}
                onPrev={handleWizardPrev}
                onSubmit={handleSubmit}
                canProceed={canProceedToNextStep()}
                isSubmitting={isSubmitting}
              >
                {renderWizardStepContent()}
              </Wizard>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Close Confirmation Dialog (B04 - Wizard state stability) */}
      <Dialog open={closeConfirmOpen} onOpenChange={setCloseConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-yellow-600" />
              Modifications non enregistrées
            </DialogTitle>
            <DialogDescription>
              Vous avez des modifications non enregistrées. Que souhaitez-vous faire ?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-2 sm:gap-0">
            <Button 
              variant="outline" 
              onClick={() => {
                // Cancel - stay in form
                setCloseConfirmOpen(false);
              }}
            >
              Continuer l'édition
            </Button>
            <Button 
              variant="secondary"
              onClick={() => {
                // Save wizard state and close
                saveToStorage(formData, currentWizardStep);
                setCloseConfirmOpen(false);
                setDialogOpen(false);
                toast.info('Brouillon sauvegardé');
              }}
            >
              Sauvegarder brouillon
            </Button>
            <Button 
              variant="destructive" 
              onClick={() => {
                // Discard changes and close
                setCloseConfirmOpen(false);
                setDialogOpen(false);
                setCurrentWizardStep(0);
                resetForm();
                clearStorage();
              }}
            >
              Abandonner
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Document Management Dialog */}
      <Dialog open={docDialogOpen} onOpenChange={setDocDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="document-dialog">
          <DialogHeader>
            <DialogTitle>Gestion des documents - {selectedVehicule?.immatriculation}</DialogTitle>
            <DialogDescription>
              Gérer les documents, la photo et l'inspection du véhicule
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Existing Documents Display */}
            {selectedVehicule?.documents && selectedVehicule.documents.length > 0 && (
              <Card className="bg-gray-50">
                <CardHeader>
                  <CardTitle className="text-sm">Documents existants</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {selectedVehicule.documents.map((doc, idx) => (
                      <div key={idx} className="flex items-center justify-between bg-white p-3 rounded border">
                        <div className="flex items-center gap-3">
                          <File className="h-4 w-4 text-gray-500" />
                          <div>
                            <p className="font-medium text-sm capitalize">
                              {doc.type_document?.replace('_', ' ')} - {doc.numero_document}
                            </p>
                            <p className="text-xs text-gray-500">
                              Expire le {new Date(doc.date_expiration).toLocaleDateString('fr-FR')} - 
                              <span className={doc.statut === 'Valide' ? 'text-green-600' : 'text-red-600'}>
                                {' '}{doc.statut}
                              </span>
                            </p>
                          </div>
                        </div>
                        {doc.fichier_url && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}${doc.fichier_url}`, '_blank')}
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Photo Upload Section */}
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Photo du véhicule</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {selectedVehicule?.photo_url && (
                  <div className="mb-3">
                    <img 
                      src={`${process.env.REACT_APP_BACKEND_URL}${selectedVehicule.photo_url}`} 
                      alt="Véhicule"
                      className="w-full max-w-md rounded border"
                    />
                  </div>
                )}
                <div className="flex gap-2">
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handlePhotoUpload(e.target.files[0])}
                    id="photo-upload"
                    className="hidden"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => document.getElementById('photo-upload').click()}
                    className="w-full"
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    {selectedVehicule?.photo_url ? 'Remplacer la photo' : 'Ajouter une photo'}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Carte Grise Upload */}
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Carte grise</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={(e) => handleDocUpload(e, 'carte-grise')} className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label htmlFor="cg-numero" className="text-xs">Numéro</Label>
                      <Input id="cg-numero" required className="h-9" />
                    </div>
                    <div>
                      <Label htmlFor="cg-emission" className="text-xs">Date d'émission</Label>
                      <Input id="cg-emission" type="date" required className="h-9" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label htmlFor="cg-expiration" className="text-xs">Date d'expiration</Label>
                      <Input id="cg-expiration" type="date" required className="h-9" />
                    </div>
                    <div>
                      <Label htmlFor="cg-file" className="text-xs">Fichier (PDF/PNG)</Label>
                      <Input id="cg-file" type="file" accept=".pdf,.png,.jpg,.jpeg" required className="h-9" />
                    </div>
                  </div>
                  <Button type="submit" size="sm" className="w-full bg-emerald-600 hover:bg-emerald-700">
                    <Upload className="h-3 w-3 mr-2" />
                    Enregistrer la carte grise
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Assurance Upload */}
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Assurance</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={(e) => handleDocUpload(e, 'assurance')} className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label htmlFor="ass-numero" className="text-xs">Numéro</Label>
                      <Input id="ass-numero" required className="h-9" />
                    </div>
                    <div>
                      <Label htmlFor="ass-emission" className="text-xs">Date d'émission</Label>
                      <Input id="ass-emission" type="date" required className="h-9" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label htmlFor="ass-expiration" className="text-xs">Date d'expiration</Label>
                      <Input id="ass-expiration" type="date" required className="h-9" />
                    </div>
                    <div>
                      <Label htmlFor="ass-file" className="text-xs">Fichier (PDF/PNG)</Label>
                      <Input id="ass-file" type="file" accept=".pdf,.png,.jpg,.jpeg" required className="h-9" />
                    </div>
                  </div>
                  <Button type="submit" size="sm" className="w-full bg-emerald-600 hover:bg-emerald-700">
                    <Upload className="h-3 w-3 mr-2" />
                    Enregistrer l'assurance
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Permis de Conduire Upload */}
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Permis de conduire</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={(e) => handleDocUpload(e, 'permis-conduire')} className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label htmlFor="pc-numero" className="text-xs">Numéro</Label>
                      <Input id="pc-numero" required className="h-9" />
                    </div>
                    <div>
                      <Label htmlFor="pc-emission" className="text-xs">Date d'émission</Label>
                      <Input id="pc-emission" type="date" required className="h-9" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label htmlFor="pc-expiration" className="text-xs">Date d'expiration</Label>
                      <Input id="pc-expiration" type="date" required className="h-9" />
                    </div>
                    <div>
                      <Label htmlFor="pc-file" className="text-xs">Fichier (PDF/PNG)</Label>
                      <Input id="pc-file" type="file" accept=".pdf,.png,.jpg,.jpeg" required className="h-9" />
                    </div>
                  </div>
                  <Button type="submit" size="sm" className="w-full bg-emerald-600 hover:bg-emerald-700">
                    <Upload className="h-3 w-3 mr-2" />
                    Enregistrer le permis
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Inspection Details */}
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Détails d'inspection</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleInspectionUpdate} className="space-y-3">
                  <div className="flex items-center space-x-2">
                    <Checkbox 
                      id="inspection-done" 
                      checked={selectedVehicule?.inspection_realisee || false}
                      onCheckedChange={(checked) => 
                        setSelectedVehicule({...selectedVehicule, inspection_realisee: checked})
                      }
                    />
                    <Label htmlFor="inspection-done" className="text-sm">
                      Inspection réalisée
                    </Label>
                  </div>
                  {selectedVehicule?.inspection_realisee && (
                    <>
                      <div>
                        <Label htmlFor="inspection-date" className="text-xs">Date d'inspection</Label>
                        <Input 
                          id="inspection-date" 
                          type="date" 
                          defaultValue={selectedVehicule?.date_inspection}
                          className="h-9" 
                        />
                      </div>
                      <div>
                        <Label htmlFor="inspection-remarks" className="text-xs">Remarques</Label>
                        <Input 
                          id="inspection-remarks" 
                          defaultValue={selectedVehicule?.remarque_inspection}
                          placeholder="Remarques sur l'inspection..."
                          className="h-9" 
                        />
                      </div>
                    </>
                  )}
                  <Button type="submit" size="sm" className="w-full bg-emerald-600 hover:bg-emerald-700">
                    Enregistrer l'inspection
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setDocDialogOpen(false)}>
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmer la suppression</DialogTitle>
            <DialogDescription>
              Êtes-vous sûr de vouloir supprimer {selectedIds.length} véhicule(s) ? 
              Cette action est irréversible et supprimera également tous les documents associés.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirmOpen(false)}>
              Annuler
            </Button>
            <Button variant="destructive" onClick={confirmBulkDelete}>
              Supprimer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>

  );
};

export default Vehicules;
